import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, Camera, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import useDeviceDetection from '../hooks/useDeviceDetection';

const FileUpload = ({ 
  label, 
  value, 
  onChange, 
  accept = "image/*", 
  required = false, 
  API, 
  getAuthHeader,
  entityType = "general",
  documentType = "document",
  entityId = null
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const { isMobile } = useDeviceDetection();

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('document_type', documentType);
      if (entityId) {
        formData.append('entity_id', entityId);
      }

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      const fileUrl = response.data.url;
      onChange(fileUrl);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      <Label>{label} {required && '*'}</Label>
      
      <div className={`flex gap-2 ${isMobile ? 'flex-col' : ''}`}>
        {/* Upload from Gallery */}
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={isMobile ? 'w-full' : 'flex-1'}
          size={isMobile ? 'sm' : 'default'}
        >
          <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
          <span className="truncate">{uploading ? 'Uploading...' : 'Choose File'}</span>
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
        />

        {/* Capture from Camera */}
        <Button
          type="button"
          variant="outline"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading}
          className={isMobile ? 'w-full' : 'flex-1'}
          size={isMobile ? 'sm' : 'default'}
        >
          <Camera className="w-4 h-4 mr-2 flex-shrink-0" />
          <span className="truncate">Take Photo</span>
        </Button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCameraCapture}
          className="hidden"
        />
      </div>

      {/* Preview */}
      {preview && (
        <div className="relative border rounded-lg p-2 bg-slate-50">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="absolute top-1 right-1 h-6 w-6 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
          {preview.startsWith('data:') || preview.startsWith('/uploads') || preview.startsWith('http') ? (
            <img 
              src={preview.startsWith('/uploads') ? `${API}${preview}` : preview} 
              alt="Preview" 
              className="max-w-full h-32 object-contain mx-auto"
            />
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <ImageIcon className="w-4 h-4" />
              <span className="truncate">{preview}</span>
            </div>
          )}
        </div>
      )}

      {/* Hidden input for form validation */}
      <Input
        type="hidden"
        value={value || ''}
        required={required}
      />
    </div>
  );
};

export default FileUpload;
